import * as z from 'zod';
import { FLAG_TYPES } from './flags';

/**
 * One mention of an alarm topic, annotated by the model. The model never decides whether it
 * is dangerous; it only describes the mention, and triageMentions() decides.
 */
export const MentionSchema = z.object({
  type: z.enum(FLAG_TYPES),
  about: z.enum(['self', 'other']),
  negated: z.boolean(),
  mode: z.enum(['real', 'figurative', 'hypothetical', 'media']),
  when: z.enum(['today', 'recent', 'long_ago', 'unknown']),
  confidence: z.enum(['high', 'low']),
  evidence: z.string(),
});
export type Mention = z.infer<typeof MentionSchema>;

export const ComplaintSchema = z.object({
  what: z.string().min(1),
  severity: z.enum(['mild', 'moderate', 'severe']),
});
export type Complaint = z.infer<typeof ComplaintSchema>;

export const MoodSchema = z.enum(['good', 'neutral', 'low']);
export type Mood = z.infer<typeof MoodSchema>;

/**
 * Exactly what the summary model returns for one call (prompts/summary.md).
 * `null` means "not discussed" — the model must not guess.
 */
export const CallExtractionSchema = z.object({
  mood: MoodSchema.nullable(),
  sleep: z.enum(['ok', 'poor']).nullable(),
  meds_taken: z.boolean().nullable(),
  ate: z.boolean().nullable(),
  went_out_or_plans: z.string().nullable(),
  complaints: z.array(ComplaintSchema),
  mentions: z.array(MentionSchema),
  quote: z.string().nullable(),
  summary_ru: z.string().min(1),
});
export type CallExtraction = z.infer<typeof CallExtractionSchema>;

/** JSON Schema for structured outputs, generated from the same source of truth. */
export const callExtractionJsonSchema = (): Record<string, unknown> =>
  z.toJSONSchema(CallExtractionSchema, { target: 'draft-7' });
